import type { StatementLine, StatementLineType, SupplierStatement } from '../../lib/supplierStatementApi';
import { formatDate } from '../../utils/dateFormat';

/**
 * How a statement reads on screen, on paper and in a spreadsheet. Display only: every figure is
 * the server's, and nothing here adds, subtracts or re-rounds one (D49 phase 3).
 */

/** What each kind of line is called. The client never reads the server's prose to decide this. */
export const TYPE_LABEL: Record<StatementLineType, string> = {
  invoice: 'Invoice',
  payment: 'Payment',
  credit: 'Credit',
  credit_reversal: 'Credit reversed',
  adjustment: 'Paid, no payment record',
};

/** A money figure to the cent with thousands separators; blank when there is no figure. */
export function formatAmount(value: number | null | undefined): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A debit or credit column: nothing on the side a line does not touch. */
export function formatSide(value: number): string {
  return value === 0 ? '' : formatAmount(value);
}

/** "01/08/2026 to 21/09/2026", in the business's own dd/mm/yyyy. */
export function periodLabel(from: string, to: string): string {
  return `${formatDate(from, '--')} to ${formatDate(to, '--')}`;
}

/** A file name a browser and a filesystem both accept. */
export function statementFileName(statement: SupplierStatement): string {
  const safe = (text: string) => text.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return `statement-${safe(statement.supplierName) || 'supplier'}-${safe(statement.currency || '')}-${statement.from}-${statement.to}.csv`;
}

export const CSV_HEADERS = ['Date', 'Type', 'Reference', 'Description', 'Debit', 'Credit', 'Balance', 'Date estimated', 'Currency'];

function csvRow(statement: SupplierStatement, line: StatementLine): Record<string, unknown> {
  return {
    Date: formatDate(line.date, ''),
    Type: TYPE_LABEL[line.type] || line.type,
    Reference: line.reference,
    Description: line.description,
    // Blank, not 0, on the side a line does not touch, so a column of figures reads as figures.
    Debit: line.debit === 0 ? '' : line.debit,
    Credit: line.credit === 0 ? '' : line.credit,
    Balance: line.balance,
    'Date estimated': line.dateEstimated ? 'Yes' : '',
    Currency: statement.currency || '',
  };
}

/**
 * The whole statement as spreadsheet rows: an opening row, every line, a closing row. A file that
 * left out the opening balance would not add up to its own closing balance.
 */
export function statementCsvRows(statement: SupplierStatement): Array<Record<string, unknown>> {
  const bookend = (day: string, type: string, balance: number | null): Record<string, unknown> => ({
    Date: formatDate(day, ''),
    Type: type,
    Reference: '',
    Description: '',
    Debit: '',
    Credit: '',
    Balance: balance ?? '',
    'Date estimated': '',
    Currency: statement.currency || '',
  });
  return [
    bookend(statement.from, 'Opening balance', statement.opening),
    ...statement.lines.map((line) => csvRow(statement, line)),
    bookend(statement.to, 'Closing balance', statement.closing),
  ];
}
